import { useState } from "react";

interface Props {
  sms: number;
  lms: number;
  mms: number;
}

export default function SendTypeChart({ sms, lms, mms }: Props) {
  const total = sms + lms + mms;
  if (total === 0) {
    return (
      <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6 h-full flex flex-col">
        <div>
          <h3 className="text-white font-semibold text-base">발송 유형 분포</h3>
          <p className="text-gray-500 text-xs mt-0.5">SMS / LMS / MMS 비율</p>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-600 text-sm">발송 데이터가 없습니다</p>
        </div>
      </div>
    );
  }

  const smsPct = Math.round((sms / total) * 100);
  const lmsPct = Math.round((lms / total) * 100);
  const mmsPct = Math.round((mms / total) * 100);

  // Calculate donut segments
  const circumference = 2 * Math.PI * 40; // r=40
  const smsOffset = 0;
  const lmsOffset = (sms / total) * circumference;
  const mmsOffset = ((sms + lms) / total) * circumference;

  const smsDash = (sms / total) * circumference;
  const lmsDash = (lms / total) * circumference;
  const mmsDash = (mms / total) * circumference;

  const data = [
    { label: "SMS", value: sms, pct: smsPct, color: "#8b5cf6", dash: smsDash, offset: 0 },
    { label: "LMS", value: lms, pct: lmsPct, color: "#ec4899", dash: lmsDash, offset: lmsOffset },
    { label: "MMS", value: mms, pct: mmsPct, color: "#f59e0b", dash: mmsDash, offset: mmsOffset },
  ];

  return (
    <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6 h-full flex flex-col">
      <div>
        <h3 className="text-white font-semibold text-base">발송 유형 분포</h3>
        <p className="text-gray-500 text-xs mt-0.5">SMS / LMS / MMS 비율</p>
      </div>
      <div className="flex-1 flex items-center justify-center py-4">
        <div className="relative w-32 h-32">
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
            <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="12" />
            {data.map((d) => (
              <circle
                key={d.label}
                cx="50"
                cy="50"
                r="40"
                fill="none"
                stroke={d.color}
                strokeWidth="12"
                strokeDasharray={`${d.dash} ${circumference - d.dash}`}
                strokeDashoffset={-d.offset}
                strokeLinecap="round"
              />
            ))}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-white font-bold text-lg">{total.toLocaleString()}</span>
            <span className="text-gray-500 text-[10px]">총 발송</span>
          </div>
        </div>
      </div>
      <div className="space-y-2">
        {data.map((d) => (
          <div key={d.label} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: d.color }} />
              <span className="text-gray-400 text-xs">{d.label}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-white text-xs font-medium">{d.value.toLocaleString()}건</span>
              <span className="text-gray-500 text-xs w-8 text-right">{d.pct}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}